/**
 * ChatPersonaPicker — full-screen list of the people the astrologer can talk about: «Я» first, then the
 * saved charts. Tapping a row opens that person's latest conversation, or a new one when there is none.
 * «Управлять картами» leads to the charts page (add, edit, delete).
 *
 * Closing: Escape, the close button. Focus goes to the close button on open and back to the opener on close.
 */
import { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useCharts, type ChartItem } from '../../hooks/useCharts.js';
import { useAllChatThreads } from '../../hooks/useChatThreads.js';
import { formatShortDate } from '../../lib/format-date.js';
import { personaChatHref } from '../../lib/chat-links.js';
import { relationDisplayKey } from '../../lib/relation.js';
import { personaName } from '../../lib/chat-persona.js';
import styles from './ChatPersonaPicker.module.css';

export interface ChatPersonaPickerProps {
  /** Chart of the open conversation: null = the own chart, undefined = not known yet. */
  currentChartId: string | null | undefined;
  onClose: () => void;
  /** A person was chosen: where to go. */
  onPick: (href: string) => void;
}

export function ChatPersonaPicker({ currentChartId, onClose, onPick }: ChatPersonaPickerProps): JSX.Element {
  const { t, i18n } = useTranslation(['home', 'me', 'common']);
  const navigate = useNavigate();
  const { charts, isLoading, isError, refetch } = useCharts();
  const { threads } = useAllChatThreads();
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: KeyboardEvent): void => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      onCloseRef.current();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      if (opener && opener.isConnected) opener.focus();
    };
  }, []);

  // «Я» first, then the others in the order the charts page shows them.
  const ordered = [...charts.filter((c) => c.isSelf), ...charts.filter((c) => !c.isSelf)];

  const isCurrent = (c: ChartItem): boolean =>
    c.isSelf ? currentChartId === null || currentChartId === c.id : currentChartId === c.id;

  const lastOf = (c: ChartItem): string | null => {
    const th = threads.find((x) => x.chartId === c.id || (c.isSelf && x.chartId === null));
    return th ? formatShortDate(th.lastMessageAt, i18n.language) : null;
  };

  return (
    <div className={styles.root} role="dialog" aria-modal="true" aria-labelledby="chat-persona-title" data-testid="chat-persona-picker">
      <div className={styles.column}>
        <div className={styles.head}>
          <h2 id="chat-persona-title" className={styles.title}>{t('chat.persona.title')}</h2>
          <button
            ref={closeRef}
            type="button"
            className={styles.close}
            onClick={onClose}
            aria-label={t('chat.history.close')}
            data-testid="chat-persona-close"
          >
            <svg viewBox="0 0 24 24" width="22" height="22" aria-hidden="true" focusable="false" fill="none" stroke="currentColor" strokeWidth="1.9" strokeLinecap="round">
              <path d="M6 6l12 12M18 6L6 18" />
            </svg>
          </button>
        </div>

        <div className={styles.body}>
          {isLoading && <p className={styles.note} data-testid="chat-persona-loading">{t('common:states.loading')}</p>}
          {isError && charts.length === 0 && (
            <div className={styles.errorBox}>
              <p className={styles.error} role="alert">{t('chat.persona.loadError')}</p>
              <button type="button" className={styles.retry} onClick={refetch} data-testid="chat-persona-retry">
                {t('chat.retry')}
              </button>
            </div>
          )}
          {ordered.length > 0 && (
            <ul className={styles.list}>
              {ordered.map((c) => {
                const current = isCurrent(c);
                const last = lastOf(c);
                const notReady = !c.isSelf && c.calcStatus !== 'READY';
                const relation = t(relationDisplayKey(c.relation, c.isSelf, c.sex), { ns: 'me' });
                const name = personaName(c, t('me:charts.relation.self'));
                return (
                  <li key={c.id}>
                    <button
                      type="button"
                      className={`${styles.row} ${current ? styles.current : ''}`}
                      aria-current={current ? 'true' : undefined}
                      disabled={notReady}
                      onClick={() => (current ? onClose() : onPick(personaChatHref(c, threads)))}
                      data-testid={`chat-persona-${c.id}`}
                    >
                      <span className={styles.rowText}>
                        <span className={styles.rowName}>{name}</span>
                        <span className={styles.rowSub}>
                          {notReady ? t('chat.persona.calculating') : name === relation ? '' : relation}
                        </span>
                      </span>
                      <span className={styles.rowMeta}>{last ?? t('chat.persona.noChats')}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        <div className={styles.footer}>
          <button
            type="button"
            className={styles.manage}
            onClick={() => void navigate('/me/charts')}
            data-testid="chat-persona-manage"
          >
            {t('chat.persona.manage')}
          </button>
        </div>
      </div>
    </div>
  );
}
